import React, {useCallback, useEffect, useMemo, useState, useRef} from 'react';
import {Col, Container, Form, Row} from 'react-bootstrap';
import {useParams} from 'react-router-dom';
import {Wizard} from 'shared/components/Wizard/Wizard';
import {WizardNavigation} from 'shared/components/Wizard/WizardNavigation';
import {CBCButton, Loader, useQueryParams} from 'shared/helpers';
import {isDeviceSmall} from 'shared/helpers/DeviceSize';
import {useProductItem} from 'hooks/Product/ProductItem';
import {getTabErrors} from 'components/customer/Product/helpers/getTabErrors';
import {Formik} from 'formik';
import {Specs, InvalidProduct} from 'components/customer/Product';
import {useProductContext} from 'contexts/ProductContext';
import {useNotificationContext} from 'contexts/NotificationContext';
import {useAppContext} from 'contexts/ApplicationContext';
import {useAppDispatch, useAppSelector} from 'store/customer';
import {
    allDataCleared,
    selectIsLoadingMaterials,
} from 'components/customer/Materials/store/materialSlice';
import {clearAllEdgeFinishData} from 'components/customer/EdgeFinishes/store/edgeFinishSlice';
import ProductDescription from 'components/customer/Product/Description/ProductDescription';
import CBCDialog from 'shared/components/ConfirmationDialog/CBCDialog';
import Footer, {
    CloseEventType,
} from 'shared/components/ConfirmationDialog/Footer';
import RoomAssistantTable from 'shared/components/ConfirmationDialog/RoomAssistantTable';
import {cloneDeep} from 'lodash';
import {CancelButton} from 'shared/components/CancelButton';
import {setIsSavingProduct} from 'components/customer/Preview3DCommon/store/viewerSlice';
import styled from 'styled-components';
import {PlaceholderLoader} from 'components/customer/Product/PlaceholderLoader';
import {useProductFormHelper} from 'components/customer/Product/helpers/useProductFormHelper';
import {useAutoChangeTabOnError} from 'components/customer/Product/helpers/useAutoChangeTabOnError';
import {useLazyGetProductDataQuery} from 'components/customer/Product/store/useProductApi';
import {selectProductUpdated} from 'components/customer/Product/store/productSlice';
import {
    Event,
    genericPubSub,
} from 'components/customer/RoomPlanner/helper/GenericPubSub';

export const Product = () => {
    const productIsUpdated = useAppSelector(selectProductUpdated);
    const dispatch = useAppDispatch();
    const [changeManufacturerDescription, setChangeManufacturerDescription] =
        useState(0);
    const [formValues, setFormValues] = useState({});

    const {
        currentTab,
        initialValues,
        formSchema,
        formStructure,
        productFormHandler,
        productFieldValidator,
        tabErrors,
        setTabErrors,
        loading,
        setLoading,
        showRoomDefaultDialog,
        setShowRoomDefaultDialog,
        productFormSubmitHandler,
        cancelDeferredSubmit,
    } = useProductItem();
    const {
        setCurrentTab,
        getProductInfo,
        productDataStore,
        isLoading,
        roomAssistantContent,
        showDialog,
        hideDialog,
        isMiniBrowser,
    } = useProductContext();
    const {refreshUserData} = useAppContext();
    const {addMessages, MESSAGE_TYPES} = useNotificationContext();
    const {cabinetId, copy, product} = useQueryParams();
    const {roomId} = useParams();
    const inSizesTab = [0, 1, 2, 3, 4].includes(currentTab);
    // the description column is hidden here so the fields get the full width
    const isDrawersTab = useMemo(() => {
        const visibleTabs = (formStructure || []).filter(
            (structure) =>
                structure?.layout != 'sidebar' &&
                (structure.options?.visible ?? true)
        );

        return visibleTabs[Number(currentTab)]?.name === 'Drawers';
    }, [formStructure, currentTab]);
    const wizardHeaderRef = useRef(null);
    const isRoomPlannerUpdate = useMemo(() => {
        return cabinetId && !copy && isMiniBrowser;
    }, [cabinetId, copy, isMiniBrowser]);
    const isMaterialsLoading = useAppSelector(selectIsLoadingMaterials);
    const formikRef = useRef(null);

    const [getProductData, {data: productData}] = useLazyGetProductDataQuery();
    useEffect(() => {
        getProductData(
            {
                cabinetType: Number(product),
                roomId: Number(roomId),
                cabinetId: Number(cabinetId),
            },
            true
        );
    }, [getProductData, product, cabinetId]);
    useAutoChangeTabOnError();

    const isSmallDevice = isDeviceSmall();
    let manufacturerDescription = getProductInfo('manufacturer_description');

    let manufacturerShortDescription = '';
    if (isSmallDevice) {
        // truncate longstring if on mobile
        manufacturerShortDescription =
            manufacturerDescription && manufacturerDescription.length > 90
                ? `${manufacturerDescription.substring(0, 90)}...[Read More]`
                : manufacturerDescription;
    }

    // chnage description for small devices based on state
    manufacturerDescription = isSmallDevice
        ? !changeManufacturerDescription
            ? manufacturerShortDescription
            : `${manufacturerDescription} [Read Less]`
        : manufacturerDescription;

    useEffect(() => {
        return () => {
            dispatch(allDataCleared());
            dispatch(clearAllEdgeFinishData());
            dispatch(setIsSavingProduct(false));
        };
    }, []);

    const toggleManufacturerDescription = useCallback(
        (changeState) => (event) => {
            if (changeState) {
                setChangeManufacturerDescription(
                    !changeManufacturerDescription
                );
            }
        },
        []
    );

    let invalidProduct = false;
    if (
        productDataStore.current.hasOwnProperty('invalid_cabinet') &&
        productDataStore.current.invalid_cabinet
    ) {
        invalidProduct = true;
    }

    const imageColumn = useMemo(
        () => (
            <ProductDescription
                manufacturerDescription={manufacturerDescription}
                toggleManufacturerDescription={toggleManufacturerDescription}
                productData={productData}
                formStructure={formStructure}
                inSizesTab={inSizesTab}
                formValues={formValues}
            />
        ),
        [
            inSizesTab,
            currentTab,
            manufacturerDescription,
            productData,
            formValues,
        ]
    );

    const {wizard, maxTab} = useMemo(() => {
        let productTabEventKey;
        let maxTab = 0;
        let wizard;

        const getEventKey = () => {
            if (typeof productTabEventKey === 'undefined')
                productTabEventKey = 0;
            else productTabEventKey++;

            return productTabEventKey;
        };

        let productConfig = JSON.parse(JSON.stringify(formStructure));

        if (productConfig && productConfig.length > 0) {
            productConfig = productConfig.filter(
                (structure) =>
                    structure?.layout != 'sidebar' &&
                    (structure.options?.visible ?? true)
            );

            maxTab = productConfig.length - 1;

            wizard = (
                <Wizard
                    isMiniBrowser={isMiniBrowser}
                    tabErrors={tabErrors}
                    currentTab={currentTab}
                    setCurrentTab={setCurrentTab}
                    id="product-page-wizard"
                    inSizesTab={inSizesTab}
                    imageColumn={
                        !isSmallDevice && !isMiniBrowser && !isDrawersTab
                            ? imageColumn
                            : null
                    }
                    wizardHeaderRef={wizardHeaderRef}>
                    {productConfig.map((config, index) => (
                        <Specs
                            title={config.name}
                            preview={config.preview}
                            fieldsets={config.fieldsets}
                            eventKey={getEventKey()}
                            key={config.name}
                            structure={config}
                        />
                    ))}
                </Wizard>
            );
        }

        return {
            wizard,
            maxTab,
        };
    }, [
        isMiniBrowser,
        cabinetId,
        inSizesTab,
        currentTab,
        formStructure,
        tabErrors,
        isSmallDevice,
        wizardHeaderRef,
        imageColumn,
        isDrawersTab,
    ]);

    const dynamicColumns = useMemo(() => {
        if (wizard) {
            if (!isSmallDevice) {
                return (
                    <Row>
                        <Col
                            style={isMiniBrowser ? {padding: '0'} : {}}
                            className="product-form-fields">
                            {wizard}
                        </Col>
                    </Row>
                );
            } else {
                return (
                    <Row>
                        {isDrawersTab ? null : imageColumn}
                        <Col className="product-form-fields">{wizard}</Col>
                    </Row>
                );
            }
        }
    }, [wizard, imageColumn, isSmallDevice, isDrawersTab]);

    const toggleRoomAssistantDialogues = useCallback(
        (event) => {
            dispatch(setIsSavingProduct(false));
            setShowRoomDefaultDialog(!showRoomDefaultDialog);
            if (event == CloseEventType.APPLY) {
                setLoading(true);
                productFormSubmitHandler(roomAssistantContent);
            }
            if (event == CloseEventType.CANCEL) {
                setLoading(false);
                // Continue Editing: no save happens, so anything waiting on this
                // submit (the 3DRP unsaved-changes dialog) stops waiting and the
                // user stays on the cabinet they were editing.
                cancelDeferredSubmit();
            }
            if (event == CloseEventType.OK) {
                setLoading(true);
                const data = cloneDeep(roomAssistantContent);
                data['changed_data'].map((data) => {
                    data.items.map((defaultData) => {
                        defaultData.apply_to_room = false;
                    });
                });
                productFormSubmitHandler(data);
            }
        },
        [showRoomDefaultDialog, roomAssistantContent, cancelDeferredSubmit]
    );

    const handleCancelClick = useCallback(() => {
        setShowRoomDefaultDialog(false);
        showDialog({
            title: 'Are you sure you want to turn off Room Assistant?',
            message:
                'Your product changes will be saved and the Room Assistant feature will be turned off.<br/><br/>These changes will not affect your room settings, or the defaults for future products that you add to this order.<br/><br/> <i>(You can reactivate the Room Assistant feature through Account Settings > Settings > Use Room Assistant)</i>',
            hideYesButton: true,
            hideNoButton: true,
            keyboard: false,
            buttons: [
                {
                    name: 'Cancel',
                    alignLeft: true,
                    variant: 'danger',
                    show: true,
                    action: async () => {
                        hideDialog();
                        setLoading(false);
                        cancelDeferredSubmit();
                    },
                },
                {
                    name: 'Turn Off For This Job',
                    show: true,
                    action: async () => {
                        setLoading(true);
                        const data = cloneDeep(roomAssistantContent);
                        data['toggle_room_assistant_for_job'] = false;
                        await productFormSubmitHandler(data);
                        refreshUserData();
                    },
                },
                {
                    name: 'Turn Off',
                    show: true,
                    action: async () => {
                        setLoading(true);
                        const data = cloneDeep(roomAssistantContent);
                        data['toggle_room_assistant'] = false;
                        await productFormSubmitHandler(data);
                        refreshUserData();
                    },
                },
            ],
        });
        setLoading(false);
    }, [roomAssistantContent, cancelDeferredSubmit]);

    const handleProductSubmit = useCallback(
        (submitForm, errors) => {
            return async () => {
                const errorFields = Object.entries(errors);
                if (errorFields?.length === 0) {
                    // display the loader if no formik errors
                    dispatch(setIsSavingProduct(true));
                    const saved = await submitForm();
                    return saved === true;
                } else {
                    const messages = errorFields.map(([field, errors]) => {
                        let messages = errors;

                        if (Array.isArray(errors)) {
                            messages = errors.map((error) => {
                                if (typeof error == 'object') {
                                    return Object.values(error);
                                }
                            });

                            const seen = new Set();
                            messages = messages.filter(
                                (item) =>
                                    item &&
                                    !seen.has(JSON.stringify(item)) &&
                                    seen.add(JSON.stringify(item))
                            );
                        }

                        return {
                            message: messages,
                            fields: [field],
                        };
                    });

                    addMessages({
                        type: MESSAGE_TYPES.ERROR,
                        messages,
                    });
                    setTabErrors(getTabErrors(formStructure, messages));
                    return false;
                }
            };
        },
        [formStructure]
    );

    const showForm = useMemo(() => {
        return (
            initialValues &&
            formSchema &&
            Object.keys(formSchema).length &&
            Object.keys(initialValues).length
        );
    }, [initialValues, formSchema]);

    useEffect(() => {
        const submitProductEditor = async () => {
            if (!formikRef.current) {
                return false;
            }

            const {submitForm, errors} = formikRef.current;
            const handler = handleProductSubmit(submitForm, errors);
            return handler();
        };

        genericPubSub.subscribe(
            Event.ROOM_PLANNER_SUBMIT_PRODUCT_EDITOR,
            submitProductEditor
        );

        return () => {
            genericPubSub.unsubscribe(
                Event.ROOM_PLANNER_SUBMIT_PRODUCT_EDITOR,
                submitProductEditor
            );
        };
    }, [handleProductSubmit]);

    return (
        <>
            {loading && isMiniBrowser ? <PlaceholderLoader /> : null}
            <CBCDialog
                display={showRoomDefaultDialog}
                title="Update Product Defaults?"
                footer={
                    <Footer
                        yesButtonText={`Save & Update Defaults`}
                        noButtonText={`Continue Editing`}
                        okButtonText={`Save & Don't Update Defaults`}
                        onClose={toggleRoomAssistantDialogues}
                        okButtonClassname="button-transparent"
                    />
                }>
                <RoomAssistantTable handleClick={handleCancelClick} />
            </CBCDialog>
            <ModifiedLoader
                loader={loading}
                relative={isMiniBrowser}
                hideInitially={true}
                $isMiniEditor={isMiniBrowser}
            />
            {invalidProduct ? (
                <InvalidProduct
                    message={productDataStore.current.invalid_cabinet_message}
                />
            ) : showForm ? (
                <Formik
                    enableReinitialize={true}
                    validationSchema={formSchema}
                    initialValues={initialValues}
                    onSubmit={productFormHandler}>
                    {({
                        initialValues,
                        values,
                        errors,
                        handleSubmit,
                        submitForm,
                        validateForm,
                        touched,
                    }) => {
                        formikRef.current = {submitForm, errors};
                        useProductFormHelper({
                            productFieldValidator,
                            setFormValues,
                            formSchema,
                            isRoomPlannerUpdate,
                            tabErrors,
                            setTabErrors,
                            formStructure,
                            initialValues,
                            values,
                            touched,
                            errors,
                            validateForm,
                            productData,
                        });

                        return (
                            <Form
                                noValidate
                                onSubmit={handleSubmit}
                                className="cbc-form"
                                id="product-form"
                                style={{padding: '0'}}>
                                <Container
                                    style={isMiniBrowser ? {padding: '0'} : {}}>
                                    {dynamicColumns}
                                </Container>

                                <div className="nav-and-control">
                                    <Container>
                                        <Row>
                                            <Col
                                                md={
                                                    isMiniBrowser
                                                        ? {span: 12}
                                                        : {offset: 1, span: 10}
                                                }>
                                                <WizardNavigation
                                                    currentTab={currentTab}
                                                    setCurrentTab={
                                                        setCurrentTab
                                                    }
                                                    max={maxTab}
                                                    dots={maxTab + 1}
                                                    wizardHeaderRef={
                                                        wizardHeaderRef
                                                    }
                                                />
                                            </Col>
                                        </Row>

                                        <Row
                                            style={
                                                isMiniBrowser
                                                    ? {padding: '15px 0'}
                                                    : {}
                                            }
                                            className="control-buttons">
                                            <Col
                                                xs={6}
                                                md={
                                                    isMiniBrowser
                                                        ? {span: 6}
                                                        : {offset: 2, span: 4}
                                                }>
                                                <CancelButton
                                                    noRedirect={isMiniBrowser}
                                                    noDialog={
                                                        isMiniBrowser ||
                                                        !productIsUpdated
                                                    }
                                                    onSave={handleProductSubmit(
                                                        submitForm,
                                                        errors
                                                    )}
                                                />
                                            </Col>
                                            <Col
                                                xs={6}
                                                md={isMiniBrowser ? 6 : 4}>
                                                <CBCButton
                                                    disabled={
                                                        isLoading ||
                                                        isMaterialsLoading
                                                    }
                                                    id="product-submit-button"
                                                    type="button"
                                                    onClick={handleProductSubmit(
                                                        submitForm,
                                                        errors
                                                    )}
                                                    iconName="Button-Tick.svg"
                                                    className="job-button button-blue">
                                                    {cabinetId && !copy
                                                        ? isMiniBrowser
                                                            ? 'Update'
                                                            : 'Update Product'
                                                        : 'Save Product'}
                                                </CBCButton>
                                            </Col>
                                        </Row>
                                    </Container>
                                </div>
                            </Form>
                        );
                    }}
                </Formik>
            ) : null}
        </>
    );
};

const ModifiedLoader = styled(Loader)`
    ${({$isMiniEditor}) => {
        if ($isMiniEditor) {
            return `
                opacity: 1;
                background: rgba(255, 255, 255, 0.1) !important;
            `;
        }
    }}
`;
